import React, { useEffect, useState } from "react";
import { Box, Flex } from "@chakra-ui/react";
import {
  Calendar,
  CalendarClock,
  CandlestickChart,
  Globe,
  Hash,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  METRIC_TOOLTIP_BODY_CLASS,
  METRIC_TOOLTIP_TITLE_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { normalizeReportTypeKey } from "../../../../utils/reportTypeRules";

export const REPORTS_STRATEGY_BADGE_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none bg-[image:var(--gradient-primary)]";

const REPORTS_TYPE_BADGE_BASE_CLASS =
  "border-0 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-white shadow-none";

/** Default / Performance Review — primary gradient. */
export const REPORTS_TYPE_BADGE_CLASS = `${REPORTS_TYPE_BADGE_BASE_CLASS} bg-[image:var(--gradient-primary)]`;

/** Entry Analysis — glacial purple. */
export const REPORTS_TYPE_BADGE_REGIME_CLASS = `${REPORTS_TYPE_BADGE_BASE_CLASS} bg-[image:var(--gradient-glacial-purple)]`;

/** Exit Placement — glacial orange. */
export const REPORTS_TYPE_BADGE_EXIT_PLACEMENT_CLASS = `${REPORTS_TYPE_BADGE_BASE_CLASS} bg-[image:var(--gradient-glacial-orange)]`;

/** Gradient class for a report type badge (key or display label). */
export function getReportTypeBadgeClass(reportType?: string | null): string {
  const key = getReportTypeBadgeDataType(reportType);
  if (key === "market-context-performance") return REPORTS_TYPE_BADGE_REGIME_CLASS;
  if (key === "trade-management") return REPORTS_TYPE_BADGE_EXIT_PLACEMENT_CLASS;
  return REPORTS_TYPE_BADGE_CLASS;
}

/** Normalized `data-report-type` value for CSS gradient overrides. */
export function getReportTypeBadgeDataType(reportType?: string | null): string {
  const key = normalizeReportTypeKey(reportType ?? undefined);
  if (
    key === "trade-analysis" ||
    key === "market-context-performance" ||
    key === "trade-management"
  ) {
    return key;
  }
  const label = String(reportType ?? "").trim().toLowerCase();
  if (label.includes("exit placement")) return "trade-management";
  if (label.includes("regime")) return "market-context-performance";
  if (label.includes("performance")) return "trade-analysis";
  return key || "trade-analysis";
}

/** Symbol / outline meta chips beside the type badge. */
export const REPORTS_SYMBOL_BADGE_CLASS =
  "border-gray-500 bg-transparent font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal text-gray-500 shadow-none dark:border-white dark:text-white";

/** Meta row — matches shadcn report badges (`text-xs`, `h-5`). */
export const REPORTS_CARD_META_TEXT_CLASS =
  "inline-flex h-5 max-h-5 shrink-0 items-center font-[family-name:var(--font-primary)] text-xs font-normal leading-none";

export const REPORTS_CARD_META_TRUNCATE_CLASS = `${REPORTS_CARD_META_TEXT_CLASS} min-w-0 truncate`;

const REPORTS_META_CHIP_BUTTON_CLASS =
  "aspect-square size-7 min-h-7 min-w-7 shrink-0 rounded-[6px] border-gray-500/70 bg-transparent p-0 text-gray-500 shadow-none hover:bg-muted/60 dark:border-white/80 dark:text-white dark:hover:bg-white/10";

function usePrefersHover(): boolean {
  const [prefersHover, setPrefersHover] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setPrefersHover(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return prefersHover;
}

/** Compact icon chip — hover (desktop) or tap (touch) reveals label + value. */
export function ReportsMetaIconChip({
  id,
  label,
  value,
  icon: Icon,
  valueNode,
}: {
  id: string;
  label: string;
  value: string;
  icon: LucideIcon;
  valueNode?: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const prefersHover = usePrefersHover();
  const displayValue = valueNode ?? value;
  const ariaValue = typeof displayValue === "string" ? displayValue : value;

  const reveal = (next: boolean) => {
    setOpen(next);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!prefersHover) reveal(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline-gray"
          size="icon-xs"
          data-font="primary"
          data-reports-meta-chip={id}
          className={REPORTS_META_CHIP_BUTTON_CLASS}
          aria-label={`${label}: ${ariaValue}`}
          onPointerEnter={() => {
            if (prefersHover) reveal(true);
          }}
          onPointerLeave={() => {
            if (prefersHover) reveal(false);
          }}
        >
          <Icon className="size-3.5 shrink-0" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={6}
        className={cn(
          metricTooltipShellClassName("w-auto max-w-[min(100vw-2rem,20rem)]"),
          "gap-0 p-2.5 shadow-[0_0_12px_rgba(15,23,42,0.12),0_0_2px_rgba(15,23,42,0.08)] dark:shadow-[0_0_14px_rgba(0,0,0,0.55),0_0_2px_rgba(0,0,0,0.35)]",
        )}
        onPointerEnter={() => {
          if (prefersHover) reveal(true);
        }}
        onPointerLeave={() => {
          if (prefersHover) reveal(false);
        }}
      >
        <p className={METRIC_TOOLTIP_TITLE_CLASS}>{label}</p>
        <div
          className={cn(
            "mt-1 break-words text-[11px] leading-snug",
            METRIC_TOOLTIP_BODY_CLASS,
          )}
        >
          {displayValue}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ReportsMetaSegment({
  id,
  name,
  value,
  valueNode,
  title,
  maxWidthClass,
}: {
  id: string;
  /** Label only — colon and spacing before the value are added automatically. */
  name: string;
  value: string;
  /** When set, rendered instead of plain `value` text (e.g. status badges). */
  valueNode?: React.ReactNode;
  title?: string;
  maxWidthClass?: string;
}): React.ReactElement {
  const label = name.trim().replace(/:+\s*$/, "");
  const wrapClass = maxWidthClass
    ? `${REPORTS_CARD_META_TRUNCATE_CLASS} ${maxWidthClass}`
    : REPORTS_CARD_META_TEXT_CLASS;

  return (
    <span data-reports-meta-item={id} className={wrapClass} title={title ?? `${label}: ${value}`}>
      <span data-reports-meta-name>{label}:</span>
      <span
        data-reports-meta-value={valueNode ? "badge" : undefined}
        className={cn("inline-flex items-center", !valueNode && "text-gray-500 dark:text-sidebar-foreground")}
      >
        {valueNode ?? value}
      </span>
    </span>
  );
}

/** Report detail card chrome stroke — parent cards, narrative, findings, etc. */
export const REPORT_DETAIL_CARD_BORDER = "var(--border)";

/** Report detail Chakra cards — theme border stroke, no drop shadow (header, overview, notes, …). */
export const reportDetailSectionCardProps = {
  "data-reports-detail-card": "",
  bg: "white",
  borderRadius: "6px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: REPORT_DETAIL_CARD_BORDER,
  boxShadow: "none",
  sx: {
    border: `1px solid ${REPORT_DETAIL_CARD_BORDER} !important`,
    borderColor: `${REPORT_DETAIL_CARD_BORDER} !important`,
    boxShadow: "none !important",
    _dark: {
      bg: "var(--sidebar)",
      color: "var(--sidebar-foreground)",
    },
  },
} as const;

/** Border-left stroke (not w-px fill) — avoids subpixel width differences when flex clips. */
export function ReportsCardMetaSeparator() {
  return (
    <Separator
      orientation="vertical"
      decorative
      data-reports-meta-separator=""
      className="mx-0 h-3 w-0 shrink-0 self-center border-0 border-l border-solid border-border bg-transparent p-0 shadow-none"
    />
  );
}

/** Same #d3d3d3 border stroke as meta dividers, horizontal between header blocks. */
export function ReportsCardSectionSeparator({ className }: { className?: string }) {
  return (
    <Separator
      orientation="horizontal"
      decorative
      data-reports-section-separator=""
      className={
        className ??
        "mt-3 h-0 w-full shrink-0 border-0 border-t border-solid border-border bg-transparent p-0 shadow-none"
      }
    />
  );
}

export function ReportsCardMetaItems({
  items,
}: {
  items: Array<{ id: string; node: React.ReactNode }>;
}) {
  if (items.length === 0) return null;

  return (
    <div
      data-reports-card-meta=""
      data-reports-card-meta-chips=""
      className="flex min-w-0 flex-wrap items-end gap-1.5"
    >
      {items.map((item) => (
        <React.Fragment key={item.id}>{item.node}</React.Fragment>
      ))}
    </div>
  );
}

export function ReportsCardMetaLine({
  reportNumber,
  accountCode,
  dateLabel,
}: {
  reportNumber: number | null | undefined;
  accountCode: string | null | undefined;
  dateLabel: string;
}) {
  const items: Array<{ id: string; node: React.ReactNode }> = [];

  if (typeof reportNumber === "number") {
    items.push({
      id: "report",
      node: (
        <ReportsMetaIconChip
          id="report"
          label="Report"
          value={`#${reportNumber}`}
          icon={Hash}
        />
      ),
    });
  }

  if (accountCode) {
    items.push({
      id: "account",
      node: (
        <ReportsMetaIconChip
          id="account"
          label="Account"
          value={accountCode}
          icon={UserRound}
        />
      ),
    });
  }

  items.push({
    id: "date",
    node: (
      <ReportsMetaIconChip
        id="date"
        label="Window"
        value={dateLabel}
        icon={Calendar}
      />
    ),
  });

  return <ReportsCardMetaItems items={items} />;
}

/** Detail title meta — account, window, created, candles, timezone (no report # / id). */
export function buildReportDetailMetaItems(options: {
  accountCode?: string | null;
  dateLabel: string;
  createdAtDisplay?: string | null;
  candleTimeframe?: string | null;
  timezoneLabel?: string | null;
}): Array<{ id: string; node: React.ReactNode }> {
  const items: Array<{ id: string; node: React.ReactNode }> = [];

  if (options.accountCode) {
    items.push({
      id: "account",
      node: (
        <ReportsMetaIconChip
          id="account"
          label="Account"
          value={options.accountCode}
          icon={UserRound}
        />
      ),
    });
  }

  items.push({
    id: "date",
    node: (
      <ReportsMetaIconChip
        id="date"
        label="Window"
        value={options.dateLabel}
        icon={Calendar}
      />
    ),
  });

  if (options.createdAtDisplay) {
    items.push({
      id: "created",
      node: (
        <ReportsMetaIconChip
          id="created"
          label="Created"
          value={options.createdAtDisplay}
          icon={CalendarClock}
        />
      ),
    });
  }

  if (options.candleTimeframe) {
    items.push({
      id: "timeframe",
      node: (
        <ReportsMetaIconChip
          id="timeframe"
          label="Candles"
          value={options.candleTimeframe}
          icon={CandlestickChart}
        />
      ),
    });
  }

  if (options.timezoneLabel) {
    items.push({
      id: "timezone",
      node: (
        <ReportsMetaIconChip
          id="timezone"
          label="Timezone"
          value={options.timezoneLabel}
          icon={Globe}
        />
      ),
    });
  }

  return items;
}

export function ReportsCardMetaBadges({
  strategyName,
  reportType,
  reportTypeLabel,
  symbol,
}: {
  strategyName?: string | null;
  /** API report type key (preferred for gradient). */
  reportType?: string | null;
  /** When omitted/empty, the type badge is hidden (e.g. simulation sheets). */
  reportTypeLabel?: string | null;
  symbol?: string | null;
}) {
  const strategyLabel = strategyName?.trim()
    ? strategyName.trim().replace(/^Strategy:\s*/i, "")
    : "";
  const typeLabel = reportTypeLabel?.trim() ?? "";
  const typeKey = getReportTypeBadgeDataType(reportType || reportTypeLabel);

  return (
    <Flex
      gap={2}
      flexShrink={0}
      align="flex-end"
      justify="flex-end"
      flexWrap="nowrap"
      alignSelf="flex-end"
      data-reports-card-header-badges=""
    >
      {strategyLabel ? (
        <Badge data-reports-strategy-badge="" className={REPORTS_STRATEGY_BADGE_CLASS}>
          {`Strategy: ${strategyLabel}`}
        </Badge>
      ) : null}
      {typeLabel ? (
        <Badge
          data-reports-type-badge=""
          data-report-type={typeKey}
          className={getReportTypeBadgeClass(typeKey)}
        >
          {typeLabel}
        </Badge>
      ) : null}
      {symbol ? (
        <Badge variant="outline" data-reports-symbol-badge="" className={REPORTS_SYMBOL_BADGE_CLASS}>
          {symbol}
        </Badge>
      ) : null}
    </Flex>
  );
}

export function ReportsCardHeaderRow({
  reportNumber,
  accountCode,
  dateLabel,
  strategyName,
  reportType,
  reportTypeLabel,
  symbol,
}: {
  reportNumber: number | null | undefined;
  accountCode: string | null | undefined;
  dateLabel: string;
  strategyName?: string | null;
  reportType?: string | null;
  reportTypeLabel?: string | null;
  symbol?: string | null;
}) {
  return (
    <Flex
      align="flex-end"
      justify="space-between"
      gap={3}
      mb={2}
      minW={0}
      w="100%"
      flexShrink={0}
      flexWrap="wrap"
      data-reports-card-meta-row=""
    >
      <Box flex="1" minW={0} mr={2}>
        <ReportsCardMetaLine
          reportNumber={reportNumber}
          accountCode={accountCode}
          dateLabel={dateLabel}
        />
      </Box>
      <ReportsCardMetaBadges
        strategyName={strategyName}
        reportType={reportType}
        reportTypeLabel={reportTypeLabel}
        symbol={symbol}
      />
    </Flex>
  );
}

/** Meta row + badges on one line (report detail, below title). */
export function ReportsCardMetaRowWithBadges({
  metaItems,
  strategyName,
  reportType,
  reportTypeLabel,
  symbol,
}: {
  metaItems: Array<{ id: string; node: React.ReactNode }>;
  strategyName?: string | null;
  reportType?: string | null;
  reportTypeLabel?: string | null;
  symbol?: string | null;
}) {
  if (metaItems.length === 0) return null;

  return (
    <Flex
      align="flex-end"
      justify="space-between"
      gap={3}
      mt={2}
      minW={0}
      w="100%"
      flexShrink={0}
      flexWrap="wrap"
      data-reports-card-meta-row=""
    >
      <Box flex="1" minW={0} mr={2}>
        <ReportsCardMetaItems items={metaItems} />
      </Box>
      <ReportsCardMetaBadges
        strategyName={strategyName}
        reportType={reportType}
        reportTypeLabel={reportTypeLabel}
        symbol={symbol}
      />
    </Flex>
  );
}
